const DB_NAME = 'EssioFileDB';
const STORE_NAME = 'files';
/**
 * 必须与 Essio 平台保持一致（Essio 侧 Cm = 1）。
 * 不可单独升版本：若 Roboneo 升到 2，Essio 仍以 1 打开会失败，落地页 → 工具页链路中断。
 */
const DB_VERSION = 1;

/**
 * 将 IDBRequest 包装为 Promise
 */
function wrapRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * IndexedDB 操作工具类（单例）
 *
 * 数据库：EssioFileDB / object store：files
 * Schema 与 Essio 对齐：无 keyPath，使用外部主键（_filekey_ / 日期时间戳等）
 */
class IndexedDBHelper {
  private static instance: IndexedDBHelper;

  private db: IDBDatabase | null = null;

  private dbReadyPromise: Promise<IDBDatabase> | null = null;

  // eslint-disable-next-line no-useless-constructor, no-empty-function, @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance(): IndexedDBHelper {
    if (!IndexedDBHelper.instance) {
      IndexedDBHelper.instance = new IndexedDBHelper();
    }
    return IndexedDBHelper.instance;
  }

  /**
   * 打开数据库连接（懒初始化，重复调用返回同一实例）
   * 建表方式与 Essio `createObjectStore(storeName, options || {})` 一致，不传 keyPath
   */
  private open(): Promise<IDBDatabase> {
    if (this.db) return Promise.resolve(this.db);
    if (this.dbReadyPromise) return this.dbReadyPromise;

    this.dbReadyPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        this.db.onclose = () => {
          this.db = null;
          this.dbReadyPromise = null;
        };
        resolve(this.db);
      };

      request.onerror = (event) => {
        this.dbReadyPromise = null;
        reject((event.target as IDBOpenDBRequest).error);
      };
    });

    return this.dbReadyPromise;
  }

  /**
   * 获取 object store 事务
   * @param mode 事务模式，默认 readonly
   */
  private async getStore(mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
    const db = await this.open();
    const tx = db.transaction(STORE_NAME, mode);
    return tx.objectStore(STORE_NAME);
  }

  /**
   * 新增一条记录；传入 key 时使用外部主键（与 Essio put(data, key) 一致），否则由 IndexedDB 自动生成
   */
  public async add<T extends Record<string, any>>(data: T, key?: IDBValidKey): Promise<IDBValidKey> {
    const store = await this.getStore('readwrite');
    if (key !== undefined) {
      return wrapRequest(store.add(data, key));
    }
    return wrapRequest(store.add(data));
  }

  /**
   * 新增或更新一条记录；传入 key 时使用外部主键（与 Essio put(data, key) 一致）
   */
  public async put<T extends Record<string, any>>(data: T, key?: IDBValidKey): Promise<IDBValidKey> {
    const store = await this.getStore('readwrite');
    if (key !== undefined) {
      return wrapRequest(store.put(data, key));
    }
    return wrapRequest(store.put(data));
  }

  /**
   * 根据主键查询单条记录
   */
  public async get<T = any>(id: IDBValidKey): Promise<T | undefined> {
    const store = await this.getStore('readonly');
    return wrapRequest(store.get(id));
  }

  /**
   * 根据主键查询并转换为可上传文件列表
   * 兼容 File / Blob / ArrayBuffer 以及 { file, name, type } 结构
   */
  public async getUploadFiles(id: IDBValidKey): Promise<File[]> {
    const record = await this.get<unknown>(id);
    if (!record) return [];

    const toFile = (raw: unknown): File | null => {
      if (raw instanceof File) return raw;
      if (raw instanceof Blob) {
        return new File([raw], 'image.jpg', { type: raw.type || 'image/jpeg' });
      }
      if (!raw || typeof raw !== 'object') return null;

      const row = raw as Record<string, unknown>;
      const candidate = row.file ?? row.blob ?? row.data;
      const name = typeof row.name === 'string' ? row.name : 'image.jpg';
      const mime = typeof row.type === 'string' ? row.type : '';

      if (candidate instanceof File) return candidate;
      if (candidate instanceof Blob) {
        return new File([candidate], name, { type: mime || candidate.type || 'application/octet-stream' });
      }
      if (candidate instanceof ArrayBuffer) {
        return new File([candidate], name, { type: mime || 'application/octet-stream' });
      }
      return null;
    };

    return (Array.isArray(record) ? record : [record])
      .map(toFile)
      .filter((file): file is File => !!file);
  }

  /**
   * 查询全部记录
   */
  public async getAll<T = any>(): Promise<T[]> {
    const store = await this.getStore('readonly');
    return wrapRequest(store.getAll());
  }

  /**
   * 根据主键删除单条记录
   */
  public async delete(id: IDBValidKey): Promise<void> {
    const store = await this.getStore('readwrite');
    return wrapRequest(store.delete(id));
  }

  /**
   * 清空所有记录
   */
  public async clear(): Promise<void> {
    const store = await this.getStore('readwrite');
    return wrapRequest(store.clear());
  }

  /**
   * 获取记录总数
   */
  public async count(): Promise<number> {
    const store = await this.getStore('readonly');
    return wrapRequest(store.count());
  }

  /**
   * 判断指定主键的记录是否存在
   */
  public async has(id: IDBValidKey): Promise<boolean> {
    const result = await this.get(id);
    return result !== undefined;
  }

  /**
   * 批量新增，在同一个事务中完成，返回所有新增记录的主键
   */
  public async bulkAdd<T extends Record<string, any>>(items: T[]): Promise<IDBValidKey[]> {
    const db = await this.open();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const requests = items.map((item) => wrapRequest(store.add(item)));
    const keys = await Promise.all(requests);

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(keys);
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * 批量删除，在同一个事务中完成
   */
  public async bulkDelete(ids: IDBValidKey[]): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    ids.forEach((id) => store.delete(id));

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * 手动关闭数据库连接
   */
  public close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.dbReadyPromise = null;
    }
  }
}

export default IndexedDBHelper.getInstance();
